import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Tab, Tabs, useTheme } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { amalgam2 } from '../../../../icons';
import PageHeader from '../../../../components/PageHeader';
import constants from '../../../../constants';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { selectCurrentUser } from '../../../../redux/user';
import {
  getSubscriptionData,
  selectAutoShipmentItems,
  selectLoadingState,
  selectSubscriptionCustomerNumber,
  selectSubscriptionItems,
  selectTimeBasedTbrItems
} from '../../../../redux/subscription';
import { AmalgamWasteItem, WasteStatus } from '../types';
import { getAmalgamWaste } from '../../../../services/wasteManagementService';
import AmalgamWasteContainersImage from '../../../../images/items/amalgam_waste_containers.png';
import { ComplianceWrapper } from '../components/ComplianceWrapper';
import AmalgamWasteContainer from './AmalgamWasteContainer';
import { sendEmail } from '../../../../services/emailService';
import { addError, addSuccess } from '../../../../redux/alerts';
import { useNavigate } from 'react-router-dom';
import useCustomerNumber from '../../../../hooks/useCustomerNumber';
import useIsCorpView from '../../../../hooks/useIsCorpView';

const initialCompliance = {
  message_long: '',
  compliance_status: 4
};

const AmalgamWaste = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const isCorpView = useIsCorpView()
  const currentUser = useAppSelector(selectCurrentUser);
  const tbrItems = useAppSelector(selectTimeBasedTbrItems);
  const subscriptionCustomerNumber = useAppSelector(selectSubscriptionCustomerNumber);
  const loadingState = useAppSelector(selectLoadingState);
  const subscriptionItems = useAppSelector(selectSubscriptionItems);
  const autoShipmentItems = useAppSelector(selectAutoShipmentItems);
  const [amalgamWaste, setAmalgamWaste] = useState<AmalgamWasteItem[]>([]);
  const [amalgamWasteCompliance, setAmalgamWasteCompliance] = useState(initialCompliance);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [emailLoading, setEmailLoading] = useState<boolean>(false);

  const customerNumber = useCustomerNumber()

  const { hasEligibleAutoShipments, hasAutoShipments } = useMemo(() => {
    if (!amalgamWaste.length || (!tbrItems.length && !subscriptionItems.length)) {
      return { hasEligibleAutoShipments: false, hasAutoShipments: false }
    }

    const amalgamWasteIds = new Set(
      amalgamWaste.map((amalgam) => amalgam.item_number.trim())
    )
    const subscriptionIds = subscriptionItems.map((item) => item.itemId.trim())
    const subscriptionMatches = subscriptionIds.filter((id) => amalgamWasteIds.has(id))

    if (subscriptionMatches.length) {
      return { hasEligibleAutoShipments: true, hasAutoShipments: true }
    }

    if (tbrItems.length) {
      const tbrEligibleIds = new Set(
        tbrItems.map((item) => item.ItemNumber.trim())
      )
      const tbrItemMatches = Array.from(amalgamWasteIds).filter((id) => tbrEligibleIds.has(id))

      if (tbrItemMatches.length) {
        return { hasEligibleAutoShipments: true, hasAutoShipments: false }
      }
    }

    return { hasEligibleAutoShipments: false, hasAutoShipments: false }
  }, [amalgamWaste, tbrItems, subscriptionItems])

  const isRenewal = (item: AmalgamWasteItem) => {
    if (!autoShipmentItems) return false;

    return !!autoShipmentItems.find((autoShipment) => parseInt(autoShipment.itemId, 10) === parseInt(item.item_number, 10));
  };

  const getAmalgamWasteData = async () => {
    const amalgamWasteResponse = await getAmalgamWaste(customerNumber);

    const {
      amalgam_containers,
      practice_amalgam_compliance,
    } = amalgamWasteResponse;

    if (practice_amalgam_compliance?.compliance) {
      setAmalgamWasteCompliance({
        message_long: practice_amalgam_compliance?.compliance.message_long,
        compliance_status: practice_amalgam_compliance?.compliance.compliance_status
      });
    }

    const amalgamContainers = amalgam_containers
      .map((amalgam: AmalgamWasteItem) => ({ ...amalgam, isRenewal: isRenewal(amalgam) }));

    setAmalgamWaste(amalgamContainers);
  };

  const sendReorderEmail = async () => {
    if (!currentUser) return;
    setEmailLoading(true);

    try {
      const emailDetails = {
        firstname: currentUser.firstname,
        lastname: currentUser.lastname,
        userEmail: currentUser.email,
        notificationName: 'amalgamContainerReorder'
      }
      await sendEmail(emailDetails, customerNumber);
      setEmailLoading(false);
      dispatch(addSuccess('Your request has been sent to one of our compliance managers. You will be contacted shortly.'));
    } catch (error) {
      console.error(error);
      dispatch(addError(error instanceof Error ? error.message : 'There was a problem sending your request, please try again later'));
      setEmailLoading(false);
    }
  };

  const getInUseItems: AmalgamWasteItem[] = useMemo(() => {
    return amalgamWaste
      .filter(amalgam => amalgam.used_date === null && amalgam.processed_date === null)
      .map(amalgam => ({ ...amalgam, disposal_status: WasteStatus.inUse }))
      .sort((a, b) => (a.shipped_date < b.shipped_date ? -1 : a.shipped_date > b.shipped_date ? 1 : 0));
  }, [amalgamWaste]);

  const getUsedItems: AmalgamWasteItem[] = useMemo(() => {
    return amalgamWaste
      .filter(amalgam => amalgam.used_date !== null && amalgam.processed_date === null)
      .map(amalgam => ({ ...amalgam, disposal_status: WasteStatus.used }))
      .sort((a, b) => {
        if (!a.used_date && !b.used_date) return 0;
        if (!a.used_date) return 1;
        if (!b.used_date) return -1;

        return a.used_date > b.used_date ? -1 : a.used_date < b.used_date ? 1 : 0;
      });
  }, [amalgamWaste]);

  const getProcessedItems: AmalgamWasteItem[] = useMemo(() => {
    return amalgamWaste
      .filter(amalgam => amalgam.processed_date !== null)
      .map(amalgam => ({ ...amalgam, disposal_status: WasteStatus.destroyed }))
      .sort((a, b) => (a.processed_date > b.processed_date ? -1 : a.processed_date < b.processed_date ? 1 : 0));
  }, [amalgamWaste]);

  useEffect(() => {
    if (customerNumber) {
      getAmalgamWasteData();

      if (loadingState === 'not-asked' || subscriptionCustomerNumber !== customerNumber) {
        dispatch(getSubscriptionData(customerNumber));
      }
    }
  }, [customerNumber]);

  const pageTabs = [`In Use(${getInUseItems.length})`, `Used(${getUsedItems.length})`, `Processed(${getProcessedItems.length})`];

  const AutoShipmentButton = () => {
    if (hasEligibleAutoShipments) {
      const link = isCorpView
        ? `/corp/practice/${customerNumber}/auto-renewals`
        : `/practice/auto-renewals`;
      return (
        <div className="flex flex-end m-b-16 m-r-16">
          {hasAutoShipments ? (
            <Button variant="contained" component="button" onClick={() => navigate(link)}>
              <EditIcon fontSize="small" /> Edit Auto Shipments
            </Button>
          ) : (
            <Button variant="contained" component="button" onClick={() => navigate(link)}>
              <AddIcon fontSize="small" /> Enroll in Auto Shipments
            </Button>
          )}
        </div>
      );
    }

    return;
  };

  const TabContent = () => {
    const containers = [ getInUseItems, getUsedItems, getProcessedItems ]
    const messages = [ null, 'There are no used items', 'There are no processed items' ]

    const wasteContainers = containers[tabIndex] 
    const hasContainers = wasteContainers.length > 0

    return (
      <Box sx={{ p: '0 16px 16px 16px' }}>
        {
          tabIndex === 0 && (
            <Box className="flex flex-center flex-align-center m-b-16">
              <img style={{ width: '125px' }} className="m-r-16" src={AmalgamWasteContainersImage} alt="Medical Waste Containers" />
              <Box className="flex flex-column">
                {
                  wasteContainers.length === 0 && (
                    <Box className="f-s-23 m-b-8">
                      Your office is currently out of Amalgam containers. Reorder now.
                    </Box>
                  )
                }
                {
                  wasteContainers.length === 1 && (
                    <Box className="f-s-23 m-b-8">
                      Only 1 Amalgam container is left in your office. Reorder now.
                    </Box>
                  )
                }
                <Box className="center">
                  <Button onClick={sendReorderEmail} variant="contained" disabled={emailLoading}>
                    Reorder Amalgam Containers
                  </Button>
                </Box>
              </Box>
            </Box>
          )
        }
        {
          hasContainers && (
            wasteContainers.map(amalgam => (
              <AmalgamWasteContainer
                key={amalgam.container_id}
                amalgam={amalgam}
                getWasteData={getAmalgamWasteData}
              />
            ))            
          )
        }
        {
          !hasContainers && (
            <Box>
              { messages[tabIndex] }
            </Box>
          )
        }
      </Box>
    )
  };

  return (
    <>
      <PageHeader src={amalgam2} headerName={'Amalgam Waste'} size={'26px'} />
      {amalgamWaste.length === 0 ? (
        <iframe className="iframe-sales-page" data-testid="sales-iframe" src={constants.AMALGAM_WASTE_SALES_PAGE_URL} />
      ) : (
        <>
          <ComplianceWrapper compliance={amalgamWasteCompliance} />
          <Box sx={{ color: theme.palette.color50555b.main }}>
            <Box>
              <AutoShipmentButton />
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-end m-b-16">
                <Tabs value={tabIndex} onChange={(e, newValue) => setTabIndex(newValue)}>
                  {pageTabs.length && pageTabs.map((tab, index) => <Tab key={index} label={tab} />)}
                </Tabs>
              </Box>
              <TabContent />
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default AmalgamWaste;
